// src/mixins/japaneseConverterFinal.js
import { toKatakana, toRomaji, toHiragana } from 'wanakana';
import kuromoji from 'kuromoji';

// Kuromoji 管理器（基于你的代码）
class KuromojiManager {
  constructor() {
    this.cachedTokenizer = null;
    this.isInitializing = false;
    this.initPromise = null;
  }

  // 初始化 Kuromoji（你的代码）
  async initKuromoji() {
    // 如果已有缓存实例，直接返回
    if (this.cachedTokenizer) {
      return Promise.resolve(this.cachedTokenizer);
    }

    // 如果正在初始化中，等待初始化完成
    if (this.isInitializing) {
      return new Promise((resolve, reject) => {
        const check = setInterval(() => {
          if (this.cachedTokenizer) {
            clearInterval(check);
            resolve(this.cachedTokenizer);
          } else if (!this.isInitializing) {
            clearInterval(check);
            reject(new Error("Kuromoji 初始化失败"));
          }
        }, 50);
      });
    }

    // 首次初始化
    this.isInitializing = true;

    return new Promise((resolve, reject) => {
      // 字典路径 - 确保 dict 文件夹在正确位置
      const dicPath = process.env.NODE_ENV === 'production'
        ? '/dict'  // 生产环境
        : process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/dict` : '/dict'; // 开发环境

      console.log('Kuromoji 字典路径:', dicPath);

      kuromoji.builder({ dicPath }).build((err, tokenizer) => {
        this.isInitializing = false;
        if (err) {
          console.error('❌ Kuromoji 初始化失败:', err);
          reject(err);
          return;
        }
        this.cachedTokenizer = tokenizer;
        console.log('✅ Kuromoji 初始化成功');
        resolve(tokenizer);
      });
    });
  }

  /**
   * 汉字转假名（修复版）
   */
  async kanjiToKatakana(kanjiText, options = {}) {
    const { toHalfWidth = false } = options;

    try {
      const tokenizer = await this.initKuromoji();
      const tokens = tokenizer.tokenize(kanjiText);

      let hiragana = "";
      tokens.forEach(token => {
        hiragana += token.reading || token.surface_form;
      });

      const katakana = toKatakana(hiragana, { passRomaji: true });

      // ===== 修改点1：统一在这里做半角/全角转换 =====
      let result = katakana;
      if (toHalfWidth) {
        result = this.toHalfWidthKatakana(katakana);
      }
      // 注意：不在这里做全角转换，因为 katakana 已经是全角

      return result;
    } catch (error) {
      console.error('汉字转换失败:', error);
      throw error;
    }
  }

  /**
   * ===== 修改点2：完全重写 toHalfWidthKatakana 方法，使用完整映射表 =====
   * 全角片假名转半角片假名（使用映射表，确保准确）
   */
  toHalfWidthKatakana(fullWidthKatakana) {
    if (!fullWidthKatakana) return '';

    // 完整映射表（经过验证）
    const fullToHalfMap = {
      // 清音
      'ア': 'ｱ', 'イ': 'ｲ', 'ウ': 'ｳ', 'エ': 'ｴ', 'オ': 'ｵ',
      'カ': 'ｶ', 'キ': 'ｷ', 'ク': 'ｸ', 'ケ': 'ｹ', 'コ': 'ｺ',
      'サ': 'ｻ', 'シ': 'ｼ', 'ス': 'ｽ', 'セ': 'ｾ', 'ソ': 'ｿ',
      'タ': 'ﾀ', 'チ': 'ﾁ', 'ツ': 'ﾂ', 'テ': 'ﾃ', 'ト': 'ﾄ',
      'ナ': 'ﾅ', 'ニ': 'ﾆ', 'ヌ': 'ﾇ', 'ネ': 'ﾈ', 'ノ': 'ﾉ',
      'ハ': 'ﾊ', 'ヒ': 'ﾋ', 'フ': 'ﾌ', 'ヘ': 'ﾍ', 'ホ': 'ﾎ',
      'マ': 'ﾏ', 'ミ': 'ﾐ', 'ム': 'ﾑ', 'メ': 'ﾒ', 'モ': 'ﾓ',
      'ヤ': 'ﾔ', 'ユ': 'ﾕ', 'ヨ': 'ﾖ',
      'ラ': 'ﾗ', 'リ': 'ﾘ', 'ル': 'ﾙ', 'レ': 'ﾚ', 'ロ': 'ﾛ',
      'ワ': 'ﾜ', 'ヲ': 'ｦ', 'ン': 'ﾝ',

      // 小写假名
      'ァ': 'ｧ', 'ィ': 'ｨ', 'ゥ': 'ｩ', 'ェ': 'ｪ', 'ォ': 'ｫ',
      'ッ': 'ｯ', 'ャ': 'ｬ', 'ュ': 'ｭ', 'ョ': 'ｮ',

      // 符号
      'ー': 'ｰ', '。': '｡', '、': '､', '「': '｢', '」': '｣', '・': '･',

      // 浊音、半浊音（直接映射为组合）
      'ガ': 'ｶﾞ', 'ギ': 'ｷﾞ', 'グ': 'ｸﾞ', 'ゲ': 'ｹﾞ', 'ゴ': 'ｺﾞ',
      'ザ': 'ｻﾞ', 'ジ': 'ｼﾞ', 'ズ': 'ｽﾞ', 'ゼ': 'ｾﾞ', 'ゾ': 'ｿﾞ',
      'ダ': 'ﾀﾞ', 'ヂ': 'ﾁﾞ', 'ヅ': 'ﾂﾞ', 'デ': 'ﾃﾞ', 'ド': 'ﾄﾞ',
      'バ': 'ﾊﾞ', 'ビ': 'ﾋﾞ', 'ブ': 'ﾌﾞ', 'ベ': 'ﾍﾞ', 'ボ': 'ﾎﾞ',
      'パ': 'ﾊﾟ', 'ピ': 'ﾋﾟ', 'プ': 'ﾌﾟ', 'ペ': 'ﾍﾟ', 'ポ': 'ﾎﾟ',
      'ヴ': 'ｳﾞ'
    };

    let result = '';
    for (let i = 0; i < fullWidthKatakana.length; i++) {
      const char = fullWidthKatakana[i];
      result += fullToHalfMap[char] || char;
    }

    return result;
  }

  /**
   * ===== 修改点3：新增 toFullWidthKatakana 方法 =====
   * 半角片假名转全角片假名（使用映射表）
   */
  toFullWidthKatakana(halfWidthKatakana) {
    if (!halfWidthKatakana) return '';

    // 半角 → 全角映射表
    const halfToFullMap = {
      'ｱ': 'ア', 'ｲ': 'イ', 'ｳ': 'ウ', 'ｴ': 'エ', 'ｵ': 'オ',
      'ｶ': 'カ', 'ｷ': 'キ', 'ｸ': 'ク', 'ｹ': 'ケ', 'ｺ': 'コ',
      'ｻ': 'サ', 'ｼ': 'シ', 'ｽ': 'ス', 'ｾ': 'セ', 'ｿ': 'ソ',
      'ﾀ': 'タ', 'ﾁ': 'チ', 'ﾂ': 'ツ', 'ﾃ': 'テ', 'ﾄ': 'ト',
      'ﾅ': 'ナ', 'ﾆ': 'ニ', 'ﾇ': 'ヌ', 'ﾈ': 'ネ', 'ﾉ': 'ノ',
      'ﾊ': 'ハ', 'ﾋ': 'ヒ', 'ﾌ': 'フ', 'ﾍ': 'ヘ', 'ﾎ': 'ホ',
      'ﾏ': 'マ', 'ﾐ': 'ミ', 'ﾑ': 'ム', 'ﾒ': 'メ', 'ﾓ': 'モ',
      'ﾔ': 'ヤ', 'ﾕ': 'ユ', 'ﾖ': 'ヨ',
      'ﾗ': 'ラ', 'ﾘ': 'リ', 'ﾙ': 'ル', 'ﾚ': 'レ', 'ﾛ': 'ロ',
      'ﾜ': 'ワ', 'ｦ': 'ヲ', 'ﾝ': 'ン',

      'ｧ': 'ァ', 'ｨ': 'ィ', 'ｩ': 'ゥ', 'ｪ': 'ェ', 'ｫ': 'ォ',
      'ｯ': 'ッ', 'ｬ': 'ャ', 'ｭ': 'ュ', 'ｮ': 'ョ',

      'ｰ': 'ー', '｡': '。', '､': '、', '｢': '「', '｣': '」', '･': '・'
    };

    // 先处理浊音、半浊音组合（2字符）
    let result = halfWidthKatakana
      .replace(/ｶﾞ/g, 'ガ').replace(/ｷﾞ/g, 'ギ').replace(/ｸﾞ/g, 'グ')
      .replace(/ｹﾞ/g, 'ゲ').replace(/ｺﾞ/g, 'ゴ')
      .replace(/ｻﾞ/g, 'ザ').replace(/ｼﾞ/g, 'ジ').replace(/ｽﾞ/g, 'ズ')
      .replace(/ｾﾞ/g, 'ゼ').replace(/ｿﾞ/g, 'ゾ')
      .replace(/ﾀﾞ/g, 'ダ').replace(/ﾁﾞ/g, 'ヂ').replace(/ﾂﾞ/g, 'ヅ')
      .replace(/ﾃﾞ/g, 'デ').replace(/ﾄﾞ/g, 'ド')
      .replace(/ﾊﾞ/g, 'バ').replace(/ﾋﾞ/g, 'ビ').replace(/ﾌﾞ/g, 'ブ')
      .replace(/ﾍﾞ/g, 'ベ').replace(/ﾎﾞ/g, 'ボ')
      .replace(/ﾊﾟ/g, 'パ').replace(/ﾋﾟ/g, 'ピ').replace(/ﾌﾟ/g, 'プ')
      .replace(/ﾍﾟ/g, 'ペ').replace(/ﾎﾟ/g, 'ポ')
      .replace(/ｳﾞ/g, 'ヴ');

    // 处理单个字符
    let finalResult = '';
    for (let i = 0; i < result.length; i++) {
      const char = result[i];
      finalResult += halfToFullMap[char] || char;
    }

    return finalResult;
  }

  // 检查是否可用
  isAvailable() {
    return this.cachedTokenizer !== null;
  }
}

// 创建单例
const kuromojiManager = new KuromojiManager();

// 备用映射表（当 Kuromoji 不可用时使用）
const FALLBACK_MAPPINGS = {
  // 核心映射
  '日本環球支付株式会社': 'ニホンカンキュウシフカブシキガイシャ',
  '株式会社': 'カブシキガイシャ',
  '有限会社': 'ユウゲンガイシャ',

  // 常见汉字
  '日': 'ニチ', '本': 'ホン', '人': 'ジン', '国': 'コク',
  '山': 'サン', '田': 'デン', '川': 'セン', '島': 'トウ'
};

export default {
  data() {
    return {
      useKuromoji: true,
      kuromojiReady: false,
      conversionCache: new Map(),
      fallbackMode: false
    };
  },

  created() {
    this.initializeConverter();
  },

  methods: {
    /**
     * 统一获取 accountId：优先 query，其次本地缓存
     */
    $getAccountId() {
      const routeAccountId = this.$route && this.$route.query ? this.$route.query.accountId : ''
      console.log('=======>$getAccountId获取 accountId:', routeAccountId)
      if (routeAccountId) {
        localStorage.setItem('accountId', routeAccountId)
        return routeAccountId
      }
      return localStorage.getItem('accountId') || ''
    },

    /**
     * 初始化转换器
     */
    async initializeConverter() {
      try {
        await kuromojiManager.initKuromoji();
        this.kuromojiReady = true;
        console.log('✅ 转换器初始化成功');
      } catch (error) {
        console.warn('❌ Kuromoji 初始化失败，使用备用方案:', error);
        this.fallbackMode = true;
        this.useKuromoji = false;
      }
    },

    /**
     * ===== 修改点4：修复备用方案转换逻辑 =====
     */
    convertWithFallback(text, options = {}) {
      const { toHalfWidth = false } = options;

      console.log('使用备用方案转换:', text, '选项:', options);

      let result = text;

      // 按长度排序，优先匹配长词
      const sortedKeys = Object.keys(FALLBACK_MAPPINGS)
        .sort((a, b) => b.length - a.length);

      let replaced = false;
      sortedKeys.forEach(key => {
        if (result.includes(key)) {
          result = result.replace(new RegExp(key, 'g'), FALLBACK_MAPPINGS[key]);
          replaced = true;
        }
      });

      // 如果有替换，使用转换结果
      if (replaced && result !== text) {
        // 已经替换成片假名，保持原样
        result = result;
      }
      // 直接使用 wanakana 转换
      else {
        result = toKatakana(text, { passRomaji: true });
      }

      // ===== 修改点5：统一在最后做半角转换 =====
      if (toHalfWidth) {
        result = kuromojiManager.toHalfWidthKatakana(result);
      }

      return result;
    },

    /**
     * ===== 修改点6：修复主转换方法中的英文数字处理 =====
     */
    async $toKatakana(text, options = {}) {
      if (!text) return '';

      const defaultOptions = {
        passRomaji: true,
        useCache: true,
        timeout: 5000,
        toHalfWidth: false
      };

      const finalOptions = { ...defaultOptions, ...options };

      // 检查缓存
      const cacheKey = `${text}_${JSON.stringify(finalOptions)}`;
      if (finalOptions.useCache && this.conversionCache.has(cacheKey)) {
        return this.conversionCache.get(cacheKey);
      }

      // 检查是否纯英文/数字
      if (/^[a-zA-Z0-9\s\.,!?\-_]+$/.test(text)) {
        // ===== 修复：这里也需要根据 toHalfWidth 处理 =====
        let result = toKatakana(text, { passRomaji: true });
        if (finalOptions.toHalfWidth) {
          result = kuromojiManager.toHalfWidthKatakana(result);
        }
        this.conversionCache.set(cacheKey, result);
        return result;
      }

      let result = '';
      const startTime = Date.now();

      try {
        if (this.useKuromoji && !this.fallbackMode) {
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('转换超时')), finalOptions.timeout);
          });

          result = await Promise.race([
            kuromojiManager.kanjiToKatakana(text, {
              toHalfWidth: finalOptions.toHalfWidth
            }),
            timeoutPromise
          ]);
        } else {
          result = this.convertWithFallback(text, {
            toHalfWidth: finalOptions.toHalfWidth
          });
        }
      } catch (error) {
        console.warn('主方案转换失败，使用备用方案:', error);
        result = this.convertWithFallback(text, {
          toHalfWidth: finalOptions.toHalfWidth
        });
      }

      // ===== 修改点7：移除重复的半角转换，因为已经在各个子方法中处理了 =====

      // 缓存结果
      if (finalOptions.useCache) {
        this.conversionCache.set(cacheKey, result);
      }

      const elapsed = Date.now() - startTime;
      console.log(`转换完成，耗时 ${elapsed}ms: "${text}" → "${result}"`);

      return result;
    },

    /**
     * 自动填充字段
     */
    async $autoFillKatakana(sourceField, targetField, options = {}) {
      if (!this.form || !this.form[sourceField]) {
        if (targetField && this.form) {
          this.$set(this.form, targetField, '');
        }
        return '';
      }

      const text = this.form[sourceField];

      try {
        const katakana = await this.$toKatakana(text, {
          timeout: 3000,
          ...options
        });

        if (targetField && this.form) {
          this.$set(this.form, targetField, katakana);
        }

        return katakana;
      } catch (error) {
        console.error('自动填充失败:', error);
        if (targetField && this.form) {
          this.$set(this.form, targetField, '');
        }
        return '';
      }
    },

    /**
     * 同步版本
     */
    $toKatakanaSync(text, options = {}) {
      if (!text) return '';

      const { toHalfWidth = false, ...wanakanaOptions } = options;
      let result = toKatakana(text, { passRomaji: true, ...wanakanaOptions });

      if (toHalfWidth) {
        result = kuromojiManager.toHalfWidthKatakana(result);
      }

      return result;
    },

    /**
     * 其他转换方法
     */
    async $toRomaji(text, options = {}) {
      const katakana = await this.$toKatakana(text, options);
      return toRomaji(katakana, options);
    },

    async $toHiragana(text, options = {}) {
      const katakana = await this.$toKatakana(text, options);
      return toHiragana(katakana, options);
    },

    $isJapanese(text) {
      if (!text) return false;
      return /[\u4e00-\u9fa5\u3040-\u309F\u30A0-\u30FF]/.test(text);
    },

    /**
     * ===== 修改点8：修复半角转全角方法 =====
     */
    $toFullWidthKatakana(halfWidthKatakana) {
      return kuromojiManager.toFullWidthKatakana(halfWidthKatakana);
    },

    /**
     * 获取转换器状态
     */
    $getConverterStatus() {
      return {
        kuromojiReady: this.kuromojiReady,
        useKuromoji: this.useKuromoji,
        fallbackMode: this.fallbackMode,
        cacheSize: this.conversionCache.size
      };
    },

    /**
     * 清空缓存
     */
    $clearCache() {
      this.conversionCache.clear();
    }
  }
};
